import React, { useCallback, useEffect } from "react";
import { Box, MainText, PressableOpacity } from "app/components/common";
import { TextInput } from "react-native";
import theme from "app/Theme/Theme";
import FastImage from "react-native-fast-image";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserFirstName,
  getUserPicture,
} from "@microdoing/shared/services/user/selectors";
import normalize from "app/utils/normalize";
import EmptyPicture from "../EmptyPicture";
import i18n from "@microdoing/shared/i18n";
import useTagUser from "app/hooks/useTagUser";
import {
  getCampaignId,
  getStepSelectedId,
} from "@microdoing/shared/services/userCampaign/selectors";
import {
  sendResponseToStepComment,
  updateStepCommentRequest,
} from "@microdoing/shared/services/userCampaign/actions";

const PICTURE_SIZE = normalize(40);

interface IProps {
  receiver: { _id: string; firstName: string };
  stepCommentId: string;
  isModifier: boolean;
  resetState: () => void;
}

const ResponseInputWeb = ({
  receiver,
  stepCommentId,
  isModifier,
  resetState,
}: IProps) => {
  const picture = useSelector(getUserPicture);
  const firstName = useSelector(getUserFirstName);
  const stepSelectedId = useSelector(getStepSelectedId);
  const campaignId = useSelector(getCampaignId);

  const dispatch = useDispatch();

  const [
    textFormatWithColor,
    textFormatInString,
    addTagUser,
    formatTextColor,
    formatTaggedUserBackToFront,
    formatTextFromToBack,
    resetAllTag,
  ] = useTagUser({ isNotFromInput: false });

  useEffect(() => {
    if (receiver._id === "") {
      resetState();
    }
  }, [receiver._id, textFormatInString]);

  useEffect(() => {
    resetAllTag();

    if (receiver._id !== "") {
      addTagUser(
        {
          userId: receiver._id,
          name: receiver.firstName,
          startposition: 0,
          endPosition: receiver.firstName.length,
        },
        { isFirstWord: true },
      );
    }
  }, [receiver._id]);

  const changeText = newString => {
    formatTextColor(newString);
  };

  const sendingResponse = () => {
    const textToSend = formatTextFromToBack();

    if (!isModifier) {
      dispatch(
        sendResponseToStepComment({
          stepCommentId,
          comment: textToSend,
          stepId: stepSelectedId,
          receiverId: receiver?._id,
          campaignId,
        }),
      );
    } else {
      dispatch(
        updateStepCommentRequest({ stepCommentId, comment: textToSend }),
      );
    }
    resetAllTag();
    resetState();
  };

  const renderImage = useCallback(() => {
    return (
      <>
        {picture ? (
          <FastImage source={{ uri: picture }} style={styles.picture} />
        ) : (
          <EmptyPicture
            size={PICTURE_SIZE}
            firstName={firstName}
            stylePicture={styles.picture}
            variant="l5-bold"
          />
        )}
      </>
    );
  }, [picture, firstName]);

  return (
    <Box flexDirection="row" alignItems="center" marginTop="xs">
      {renderImage()}
      <Box
        flex={1}
        borderWidth={1}
        borderColor="brand.500"
        borderRadius={30}
        marginLeft="xs"
        paddingHorizontal="sm"
        paddingVertical="s"
        flexDirection="row"
        alignItems="center"
      >
        <TextInput
          style={{
            outlineWidth: 0,
            width: "100%",
            height: "100%",
            textAlignVertical: "center",
            ...theme.body["l4-regular"],
          }}
          multiline
          value={textFormatInString}
          placeholder={i18n.t("write-your-comment")}
          onChangeText={changeText}
          underlineColorAndroid="transparent"
        />
        {!!textFormatInString && (
          <PressableOpacity
            onPress={sendingResponse}
            style={{ marginLeft: theme.spacing.s }}
          >
            <MainText variant="l4-semibold" color="brand.500">
              {i18n.t("send")}
            </MainText>
          </PressableOpacity>
        )}
      </Box>
    </Box>
  );
};

const styles = {
  picture: {
    height: PICTURE_SIZE,
    width: PICTURE_SIZE,
    borderRadius: 250,
    borderColor: theme.colors["neutral.white"],
    borderWidth: 2,
  },
  nameView: {
    paddingLeft: 15,
    flex: 1,
    alignItems: "flex-start",
    justifyContent: "center",
    flexDirection: "column",
  },
};

export default ResponseInputWeb;
